/**
 * [Gurumi 에디터]
 * 역할: 빈 HTML 판별·이스케이프·이미지 URL 정규화
 * 작성/수정일: 2026-07-29
 */
const EMPTY_PATTERNS = [
  '',
  '&nbsp;',
  '<p></p>',
  '<p><br></p>',
  '<p>&nbsp;</p>',
  '<div><br></div>',
  '<br>',
];

export function isEmptyHtml(html) {
  const value = String(html || '')
    .replace(/\s/g, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<p><\/p>/gi, '')
    .toLowerCase();
  return EMPTY_PATTERNS.some((p) => value === p.replace(/\s/g, '').toLowerCase()) || value === '';
}

export function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function escapeAttr(text) {
  return escapeHtml(text);
}

export function resolveImageUrl(src, origin) {
  const value = String(src || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('//')) return `${window.location.protocol}${value}`;
  if (value.startsWith('/')) return `${origin || window.location.origin}${value}`;
  return value;
}

export function isYoutubeUrl(url) {
  return /(?:youtube\.com|youtu\.be)/i.test(String(url || ''));
}

export function isHttpUrl(url) {
  return /^https?:\/\//i.test(String(url || '').trim());
}

export function preferPlainText() {
  return window.matchMedia('(max-width: 767px)').matches;
}

/**
 * [미디어 리사이즈]
 * 역할: 게시판 설정(bo_image_width) 이미지 폭 상한
 * 작성/수정일: 2026-08-09
 */
export function getBoardImageMaxWidth(fallback = 0) {
  if (typeof window === 'undefined') return fallback;
  const opts = window.g5_gurumi_opts || {};
  const fromOpts = Number(opts.imageMaxWidth || 0);
  if (fromOpts > 0) return Math.floor(fromOpts);
  return fallback > 0 ? Math.floor(fallback) : 0;
}

/**
 * [미디어 리사이즈]
 * 역할: 에디터(ProseMirror) 본문 폭을 리사이즈 상한으로 계산 (패딩 제외)
 * 작성/수정일: 2026-07-29
 */
export function getEditorContentMaxWidth(editor, fallback = 720) {
  // TipTap 마운트 전 editor.view 접근은 예외를 던짐 — 작성/수정일: 2026-07-29
  let dom = null;
  try {
    if (!editor || editor.isDestroyed) return fallback;
    dom = editor.view?.dom ?? null;
  } catch (_) {
    return fallback;
  }
  if (!dom || typeof window === 'undefined') return fallback;
  const style = window.getComputedStyle(dom);
  const padX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  const width = Math.floor(dom.clientWidth - padX);
  return width > 0 ? width : fallback;
}

/**
 * [미디어 리사이즈]
 * 역할: 본문 폭과 게시판 이미지 폭 중 작은 값을 최종 상한으로 사용
 * 작성/수정일: 2026-08-09
 */
export function getEditorMediaMaxWidth(editor, fallback = 720) {
  const contentMax = getEditorContentMaxWidth(editor, fallback);
  const boardMax = getBoardImageMaxWidth(0);
  if (boardMax > 0) {
    return Math.max(1, Math.min(contentMax, boardMax));
  }
  return contentMax;
}

/**
 * [미디어 리사이즈]
 * 역할: width/height 를 에디터·게시판 폭·종횡비에 맞게 clamp
 * 작성/수정일: 2026-07-29
 * 수정일: 2026-08-09 — 게시판 이미지 폭 상한 반영
 */
export function clampEditorMediaSize(editor, width, height, minWidth = 80) {
  const maxWidth = getEditorMediaMaxWidth(editor);
  const safeW = Math.max(minWidth, Number(width) || minWidth);
  const safeH = Math.max(1, Number(height) || 1);
  if (safeW <= maxWidth) {
    return { width: Math.round(safeW), height: Math.round(safeH), maxWidth };
  }
  const nextW = maxWidth;
  const nextH = Math.max(1, Math.round(safeH * (nextW / safeW)));
  return { width: nextW, height: nextH, maxWidth };
}

/**
 * [미디어 리사이즈]
 * 역할: ResizableNodeView.maxSize 를 현재 미디어 상한에 동기화
 * 작성/수정일: 2026-07-29
 * 수정일: 2026-08-09 — 게시판 이미지 폭 상한 반영
 */
export function syncResizableNodeMaxSize(nodeView, editor, minWidth = 80) {
  if (!nodeView) return getEditorMediaMaxWidth(editor);
  const maxWidth = getEditorMediaMaxWidth(editor);
  nodeView.maxSize = {
    width: Math.max(minWidth, maxWidth),
    height: Math.max(Math.round(maxWidth * 4), 4000),
  };
  return maxWidth;
}
